import {Action, ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';
import {Dictionary, Request} from './model';
import * as actions from './actions';


export const SopiNgrxManagerFeature = 'sopiNgrxManager';

export const reducers: ActionReducerMap<NgrxManagerModuleState> = {
  sopiNgrxManager: ngrxManagerReducer
};

export interface NgrxManagerModuleState {
  sopiNgrxManager: NgrxManagerState;
}

export interface NgrxManagerState {
  // GET
  queryStack: Dictionary<Request>;
  queryQueue: Dictionary<Request>;
  queryFailed: Array<Request>;

  // POST | PUT | DELETE
  commandStack: Dictionary<Request>;
  commandQueue: Array<Request>;
  commandFailed: Array<Request>;

  syncActive: boolean;
}

export const initialNgrxManagerState: NgrxManagerState = {
  queryStack: new Dictionary<Request>(),
  queryQueue: new Dictionary<Request>(),
  queryFailed: [],
  commandStack: new Dictionary<Request>(),
  commandQueue: [],
  commandFailed: [],

  syncActive: true
};

export function ngrxManagerReducer(state = initialNgrxManagerState, action: Action): NgrxManagerState {
  switch (action.type) {
    // QUERY
    // -----

    // STACK
    case actions.QUERY_STACK_ADD: {
      const a = <actions.QueryStackAddAction>action;
      const queryStack = new Dictionary<Request>(state.queryStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        error: null,
        date: a.date
      };
      queryStack.add(a.key, request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryStack: queryStack
      });
    }

    case actions.QUERY_STACK_REMOVE: {
      const a = <actions.QueryStackRemoveAction>action;
      const queryStack = new Dictionary<Request>(state.queryStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      queryStack.remove(a.key);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryStack: queryStack
      });
    }

    case actions.DELETE_QUERY_STACK_REQUEST: {
      const a = <actions.DeleteQueryStackRequestAction>action;
      const queryStack = new Dictionary<Request>(state.queryStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      queryStack.remove(a.request.key);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryStack: queryStack
      });
    }

    case actions.QUERY_STACK_CLEANUP: {
      const a = <actions.QueryStackCleanUpAction>action;
      const queryStack = new Dictionary<Request>(state.queryStack._values.map((item) => {
        return {key: item.key, value: item};
      }));
      const queryQueue = new Dictionary<Request>(state.queryQueue._values.map((item) => {
        return {key: item.key, value: item};
      }));

      const queryStackValues = queryStack.values();

      queryStackValues.forEach(() => {
        const request = queryStackValues.shift();
        if (request !== undefined && request !== null && !queryQueue.containsKey(request.key)) {
          queryQueue.add(request.key, request);
        }
      });

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryStack: queryStack,
        queryQueue: queryQueue
      });
    }

    // QUEUE
    case actions.QUERY_QUEUE_ADD: {
      const a = <actions.QueryQueueAddAction>action;
      const queryQueue = new Dictionary<Request>(state.queryQueue._values.map((item) => {
        return {key: item.key, value: item};
      }));

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        count: 0,
        error: null,
        date: a.date
      };

      queryQueue.add(a.key, request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryQueue: queryQueue
      });
    }

    case actions.QUERY_QUEUE_REMOVE: {
      const a = <actions.QueryQueueRemoveAction>action;
      const queryQueue = new Dictionary<Request>(state.queryQueue._values.map((item) => {
        return {key: item.key, value: item};
      }));

      queryQueue.remove(a.key);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryQueue: queryQueue
      });
    }

    case actions.DELETE_QUERY_QUEUE_REQUEST: {
      const a = <actions.DeleteQueryQueueRequestAction>action;
      const queryQueue = new Dictionary<Request>(state.queryQueue._values.map((item) => {
        return {key: item.key, value: item};
      }));

      if (a.request !== null && a.request !== undefined && a.request.key !== undefined) {
        queryQueue.remove(a.request.key);
      }

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryQueue: queryQueue
      });
    }

    // FAILED
    case actions.QUERY_FAILED_ADD: {
      const a = <actions.QueryFailedAddAction>action;

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        error: a.error,
        date: a.date
      };
      const newQueryFailed = state.queryFailed.concat([request]);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryFailed: newQueryFailed
      });
    }

    case actions.DELETE_QUERY_FAILED_REQUEST: {
      const a = <actions.DeleteQueryFailedRequestAction>action;

      const queryFailedTmp = state.queryFailed.filter(y => y !== a.request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryFailed: queryFailedTmp
      });
    }

    case actions.DELETE_ALL_QUERY_FAILED_REQUESTS: {
      const a = <actions.DeleteAllQueryFailedRequestsAction>action;

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        queryFailed: []
      });
    }


    // COMMAND
    // -------

    // STACK
    case actions.COMMAND_STACK_ADD: {
      const a = <actions.CommandStackAddAction>action;
      const commandStack = new Dictionary<Request>(state.commandStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        error: null,
        date: a.date
      };
      commandStack.add(a.key, request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandStack: commandStack
      });
    }

    case actions.COMMAND_STACK_REMOVE: {
      const a = <actions.CommandStackRemoveAction>action;
      const commandStack = new Dictionary<Request>(state.commandStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      commandStack.remove(a.key);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandStack: commandStack
      });
    }

    case actions.COMMAND_STACK_CLEANUP: {
      const a = <actions.CommandStackCleanUpAction>action;
      const commandStack = new Dictionary<Request>(state.commandStack._values.map((item) => {
        return {key: item.key, value: item};
      }));
      let commandQueue = [];
      if (Array.isArray(state.commandQueue)) {
        commandQueue = [...state.commandQueue];
      }

      const commandStackValues = commandStack.values();

      for (let i = 0; i < commandStackValues.length; i++) {
        const request = commandStackValues.shift();
        if (request !== undefined && request !== null) {
          commandStack.remove(request.key);
          if (request !== null && request !== undefined && request.action !== undefined) {
            commandQueue.unshift(request);
          }
        }
      }

      commandQueue = commandQueue.sort(function (itemA: Request, itemB: Request) {
          const dateA = new Date(itemA.action.ngrxManager.firstRequest);
          const dateB = new Date(itemB.action.ngrxManager.firstRequest);

          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        }
      );

      const traceIds = [];
      const commandQueueFiltered = commandQueue.filter(cmd => {
        const res = traceIds.findIndex((value) => value === cmd.action.ngrxManager.traceId);
        if (res === -1) {
          traceIds.push(cmd.action.ngrxManager.traceId);
          return true;
        }
        return false;
      });

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandStack: commandStack,
        commandQueue: commandQueueFiltered
      });
    }

    case actions.DELETE_COMMAND_STACK_REQUEST: {
      const a = <actions.DeleteCommandStackRequestAction>action;
      const commandStack = new Dictionary<Request>(state.commandStack._values.map((item) => {
        return {key: item.key, value: item};
      }));

      commandStack.remove(a.request.key);
      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandStack: commandStack
      });
    }


    // QUEUE
    case actions.COMMAND_QUEUE_ADD: {
      const a = <actions.CommandQueueAddAction>action;
      let commandQueue = [];

      if (Array.isArray(state.commandQueue)) {
        commandQueue = [...state.commandQueue];
      }

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        error: null,
        date: a.date
      };
      let newCommandQueue = commandQueue.concat([request]);

      newCommandQueue = newCommandQueue.sort(function (itemA: Request, itemB: Request) {
          const dateA = new Date(itemA.action.ngrxManager.firstRequest);
          const dateB = new Date(itemB.action.ngrxManager.firstRequest);

          if (dateA < dateB) {
            return -1;
          }
          if (dateA > dateB) {
            return 1;
          }
          return 0;
        }
      );

      const traceIds = [];
      const newCommandQueueFiltered = newCommandQueue.filter(cmd => {
        const res = traceIds.findIndex((value) => value === cmd.action.ngrxManager.traceId);
        if (res === -1) {
          traceIds.push(cmd.action.ngrxManager.traceId);
          return true;
        }
        return false;
      });

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandQueue: newCommandQueueFiltered
      });
    }

    case actions.DELETE_COMMAND_QUEUE_REQUEST: {
      const a = <actions.DeleteCommandQueueRequestAction>action;

      const commandQueueTmp = state.commandQueue.filter(y => y !== a.request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandQueue: commandQueueTmp
      });
    }

    // FAILED
    case actions.COMMAND_FAILED_ADD: {
      const a = <actions.CommandFailedAddAction>action;

      const request = <Request>{
        key: a.key,
        method: a.method,
        type: a.requestType,
        action: a.action,
        error: a.error,
        date: a.date
      };
      const newCommandFailed = state.commandFailed.concat([request]);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandFailed: newCommandFailed
      });
    }

    case actions.DELETE_COMMAND_FAILED_REQUEST: {
      const a = <actions.DeleteCommandFailedRequestAction>action;

      const commandFailedTmp = state.commandFailed.filter(y => y !== a.request);

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandFailed: commandFailedTmp
      });
    }

    case actions.DELETE_ALL_COMMAND_FAILED_REQUESTS: {
      const a = <actions.DeleteAllCommandFailedRequestsAction>action;

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        commandFailed: []
      });
    }


    // OTHER
    case actions.CLEAR_REQUESTS: {
      const a = <actions.ClearRequestsAction>action;

      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        initialNgrxManagerState
      });
    }


    case actions.START_SYNC: {
      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        syncActive: true
      });
    }


    case actions.STOP_SYNC: {
      return Object.assign({}, state, <NgrxManagerState>{
        ...state,
        syncActive: false
      });
    }


    default: {
      return state;
    }
  }
}


export const getModuleState = createFeatureSelector<NgrxManagerModuleState>(SopiNgrxManagerFeature);

export const getNgrxManagerState = createSelector(getModuleState, (state: NgrxManagerModuleState) => state.sopiNgrxManager);

export const getSyncActive = createSelector(getNgrxManagerState, (state: NgrxManagerState) => state.syncActive);

export const getQueryStack = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return new Dictionary<Request>(state.queryStack._values.map((item) => {
    return {key: item.key, value: item};
  }));
});
export const getQueryQueue = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return new Dictionary<Request>(state.queryQueue._values.map((item) => {
    return {key: item.key, value: item};
  }));
});
export const getQueryFailed = createSelector(getNgrxManagerState, (state: NgrxManagerState) => state.queryFailed);
export const getCommandStack = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return new Dictionary<Request>(state.commandStack._values.map((item) => {
    return {key: item.key, value: item};
  }));
});
export const getCommandQueue = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return Array.from(state.commandQueue);
});
export const getCommandFailed = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return Array.from(state.commandFailed);
});


export const getCommandFailedState = createSelector(getNgrxManagerState, (state: NgrxManagerState) => state.commandFailed);
export const getCommandStackState = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return state.commandStack._values;
});

export const getQueryFailedState = createSelector(getNgrxManagerState, (state: NgrxManagerState) => state.queryFailed);
export const getQueryQueueState = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return state.queryQueue._values;
});
export const getQueryStackState = createSelector(getNgrxManagerState, (state: NgrxManagerState) => {
  return state.queryStack._values;
});


// COUNT
export const getQueryStackCount = createSelector(getQueryStack, (queryStack: Dictionary<Request>) => {
  if (queryStack === null || queryStack._values === null) {
    return 0;
  }
  return queryStack._values.length;
});
export const getQueryQueueCount = createSelector(getQueryQueue, (queryQueue: Dictionary<Request>) => {
  if (queryQueue === null || queryQueue._values === null) {
    return 0;
  }
  return queryQueue._values.length;
});
export const getQueriesCount = createSelector(getQueryStackCount, getQueryQueueCount, (stack: number, queue: number) => {
  return stack + queue;
});
export const getQueryFailedCount = createSelector(getQueryFailed, (queryFailed: Array<Request>) => {
  if (queryFailed === null) {
    return 0;
  }
  return queryFailed.length;
});


export const getCommandStackCount = createSelector(getCommandStack, (commandStack: Dictionary<Request>) => {
  if (commandStack === null || commandStack._values === null) {
    return 0;
  }
  return commandStack._values.length;
});
export const getCommandQueueCount = createSelector(getCommandQueue, (commandQueue: Array<Request>) => {
  if (commandQueue === null) {
    return 0;
  }
  return commandQueue.length;
});
export const getCommandsCount = createSelector(getCommandStackCount, getCommandQueueCount, (stack: number, queue: number) => {
  return stack + queue;
});
export const getCommandFailedCount = createSelector(getCommandFailed, (commandFailed: Array<Request>) => {
  if (commandFailed === null) {
    return 0;
  }
  return commandFailed.length;
});
